// Valencia — whitelist-compliant clinics only.
//
// Per safe-competitor-whitelist.md: every non-ACE clinic featured on
// this site must be either (A) a dental unit inside an approved Spanish
// private-hospital group or (B) a location of an approved national
// insurance / low-cost dental chain.
//
// VERIFICATION NOTES: entries confirmed live are `verified: true`.
// Entries on the whitelist whose specific service-page the editor could
// not fetch at the time of writing are `verified: false` and must be
// spot-checked before publication.
//
// Language rule: capability language only. No ACE-lane vocabulary
// (studio, smile-design, craft, framework, author, doctrine) applied
// to any clinic on this page.

import type { Clinic } from "./clinics-marbella";

export const VALENCIA_CLINICS: Clinic[] = [
  {
    slug: "vithas-valencia-9-de-octubre-dental",
    name: "Hospital Vithas Valencia 9 de Octubre — odontología",
    location: "Calle Valle de la Ballestera, 59, 46015 Valencia",
    website: "https://vithas.es/centro/vithas-hospital-valencia-9-de-octubre/",
    verified: true,
    profile:
      "The odontología service inside Hospital Vithas Valencia 9 de Octubre — a private hospital in the Campanar district, part of the Vithas group (22 hospitals across 14 provinces). The dental service sits alongside the hospital's medical, surgical and imaging infrastructure, with dental implants explicitly listed among the treatments available.",
    labModel:
      "Hospital-affiliated laboratory arrangements. Ceramist not publicly named.",
    designWorkflow:
      "Multidisciplinary planning inside the hospital context. Digital tools available as a capability.",
    prepPhilosophy:
      "Institutional rather than individually authored.",
    ceramistProximity: "Not publicly detailed.",
    internationalHandling:
      "Vithas operates group-level international-patient services across its hospital network.",
    reviewProfile:
      "Aggregated at the hospital level. Dental-specific depth harder to isolate.",
    leadsOn: [
      "Medically-integrated dental care inside a Vithas private hospital",
      "Hospital-scale imaging and surgical support on site",
      "Insurance and private-health coverage integration at group level",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored aesthetic preparation philosophy",
    ],
  },
  {
    slug: "imed-dental-valencia",
    name: "Hospital IMED Valencia — IMED Dental",
    location: "Avenida de la Ilustración 1, 46100 Burjassot, Valencia",
    website: "https://www.imedhospitales.com/",
    verified: true,
    profile:
      "IMED Dental is the dedicated dental unit of IMED Valencia, a private general hospital in Burjassot operated by the IMED Hospitales group (13 centres across Valencia, Alicante and Murcia). Dentistry sits alongside 15 operating rooms, 24-hour emergency services, and over 100 outpatient consultation offices.",
    labModel:
      "Hospital-affiliated laboratory arrangements. Ceramist not publicly foregrounded.",
    designWorkflow:
      "Protocolised within the hospital footprint.",
    prepPhilosophy:
      "Institutional.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "IMED operates an international patient assistance centre at group level.",
    reviewProfile:
      "Aggregated at hospital level.",
    leadsOn: [
      "Medically-integrated dental unit inside a full-service IMED private hospital",
      "Hospital-scale surgical and imaging infrastructure",
      "Group-level international-patient service",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Authored preparation doctrine",
    ],
  },
  {
    slug: "sanitas-milenium-alameda-valencia",
    name: "Sanitas Dental — Milenium Alameda (Valencia)",
    location: "Calle Micer Mascó, 7, 46010 Valencia",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A central Valencia location of Sanitas Dental's Milenium network — Sanitas's insurance-branded flagship dental-clinic line (Sanitas is part of Bupa). The Alameda clinic sits near the Jardines del Real. Pricing, plan coverage and financing are structured at the national-brand level.",
    labModel:
      "Chain-standardised lab routing.",
    designWorkflow:
      "Protocolised at network level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic insurance-and-financing proposition with standard Sanitas intake.",
    reviewProfile:
      "Aggregated across the Sanitas-Milenium network.",
    leadsOn: [
      "Insurance-branded network location with accessible pricing and financing plans",
      "Coverage and billing integration with the Sanitas private-health ecosystem",
      "Extended-hours and weekend-emergency availability",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Studio-scale single-clinician authorship",
    ],
  },
  {
    slug: "vitaldent-valencia",
    name: "Vitaldent — Valencia (national chain)",
    location: "Multiple Vitaldent locations across Valencia (18 clinics citywide)",
    website: "https://clinicas.vitaldent.com/valencia",
    verified: false,
    profile:
      "A Valencia location of Vitaldent — Spain's largest national low-cost dental chain, with more than four hundred clinics countrywide and around eighteen across the Valencia metropolitan area. Positioning is price-accessible with in-house financing plans.",
    labModel:
      "Chain-standardised lab arrangements.",
    designWorkflow:
      "Protocolised at chain level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic accessible-pricing proposition with standard multilingual intake.",
    reviewProfile:
      "High volume across the national chain.",
    leadsOn: [
      "National low-cost dental chain with in-house financing and monthly payment plans",
      "Dense Valencia footprint with walk-in access across most districts",
      "Straightforward pricing structure for routine and restorative dentistry",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Single-clinician craft positioning",
    ],
  },
  {
    slug: "sanitas-milenium-guimera-valencia",
    name: "Sanitas Dental — Milenium Guimerá (Valencia)",
    location: "Gran Vía Fernando El Católico, 24, 46008 Valencia",
    website: "https://www.sanitas.es/dental/clinicas-dentales-milenium",
    verified: true,
    profile:
      "A second Valencia anchor of Sanitas Dental's Milenium network, on Gran Vía Fernando el Católico in the Extramurs district. Part of Sanitas's insurance-branded flagship dental-clinic line.",
    labModel:
      "Chain-standardised lab routing.",
    designWorkflow:
      "Protocolised at network level.",
    prepPhilosophy:
      "Network-standardised.",
    ceramistProximity: "Not publicly described.",
    internationalHandling:
      "Domestic insurance-coverage-oriented.",
    reviewProfile:
      "Aggregated at network level.",
    leadsOn: [
      "Insurance-and-financing-oriented pricing inside the Sanitas private-health ecosystem",
      "Extended-hours availability consistent across the Milenium network",
      "Coverage and billing integration with the main Spanish dental-insurance products",
    ],
    trailsOn: [
      "Named-ceramist transparency",
      "Studio-scale aesthetic authorship",
    ],
  },
];
